import { EventBus } from "./eventBus.js";
import { JobRunner } from "./jobRunner.js";
import { LockManager } from "./lockManager.js";
import { QueueManager } from "./queueManager.js";
import { getMaxAttempts } from "./retryManager.js";
import { getNextCronDate, parseCronExpression } from "./cronParser.js";
import { MemoryStorageAdapter } from "../storage/MemoryStorageAdapter.js";
import type { SilentCronXConfig, SilentCronXLogger } from "../types/config.js";
import type { SilentCronXEventHandler, SilentCronXEventName, SilentCronXEventRecord, StatusSnapshot } from "../types/events.js";
import type {
  DelayJobOptions,
  EveryJobOptions,
  JobRecord,
  JobResult,
  JobTask,
  RunNowOptions,
  ScheduleJobOptions,
  WorkerJobOptions,
  WorkerModuleReference,
} from "../types/job.js";
import type { QueueOptions, AddQueueJobOptions, QueueStats } from "../types/queue.js";
import type { RetryConfig } from "../types/retry.js";
import type { StorageAdapter } from "../types/storage.js";
import type { SilentCronXHealth } from "../types/health.js";
import { WorkerPool } from "../workers/workerPool.js";
import { JobCancelledError, SilentCronXError } from "../utils/errors.js";
import { createJobId } from "../utils/id.js";
import { createLogger } from "../utils/logger.js";
import { assertSerializablePayload } from "../utils/safeSerialize.js";
import { nowIso } from "../utils/time.js";
import { assertPositiveNumber, assertTask, validateJobName } from "../utils/validation.js";

type RuntimeEntry = {
  id: string;
  name: string;
  kind: JobRecord["kind"];
  task?: JobTask;
  cron?: string;
  intervalMs?: number;
  delayMs?: number;
  retry?: RetryConfig;
  timeoutMs: number;
  preventOverlap: boolean;
  timer?: ReturnType<typeof setTimeout>;
};

export class SilentCronX {
  private readonly config: Required<
    Pick<
      SilentCronXConfig,
      "timezone" | "maxWorkers" | "maxConcurrency" | "defaultTimeout" | "preventOverlapping" | "lockTimeout" | "eventHistoryLimit"
    >
  > &
    SilentCronXConfig;
  private readonly storage: StorageAdapter;
  private readonly eventBus: EventBus;
  private readonly runner = new JobRunner();
  private readonly workerPool: WorkerPool;
  private readonly lockManager: LockManager;
  private readonly queueManager: QueueManager;
  private readonly logger: Required<SilentCronXLogger>;
  private readonly entries = new Map<string, RuntimeEntry>();
  private readonly controllers = new Map<string, AbortController>();
  private running = false;

  constructor(config: SilentCronXConfig = {}) {
    this.config = {
      timezone: config.timezone ?? "UTC",
      maxWorkers: config.maxWorkers ?? 2,
      maxConcurrency: config.maxConcurrency ?? 50,
      defaultTimeout: config.defaultTimeout ?? 30_000,
      preventOverlapping: config.preventOverlapping ?? true,
      lockTimeout: config.lockTimeout ?? config.defaultTimeout ?? 30_000,
      eventHistoryLimit: config.eventHistoryLimit ?? 100,
      ...config,
    };
    this.eventBus = new EventBus(this.config.eventHistoryLimit);
    this.storage = config.storage && config.storage !== "memory" ? config.storage : new MemoryStorageAdapter();
    this.logger = createLogger(config);
    this.workerPool = new WorkerPool(this.config.maxWorkers);
    this.lockManager = new LockManager(this.storage, this.config.lockTimeout);
    this.queueManager = new QueueManager(this.storage, this.eventBus, this.config.maxPayloadBytes, (jobId, task, _queueName, retry, timeout) =>
      this.executeJob(jobId, task, retry, timeout)
    );
  }

  async schedule<TPayload = unknown>(name: string, options: ScheduleJobOptions<TPayload>): Promise<string> {
    validateJobName(name);
    assertTask(options.task);
    assertSerializablePayload(options.payload, this.config.maxPayloadBytes);
    parseCronExpression(options.cron);
    const id = createJobId("cron");
    const nextRunAt = getNextCronDate(options.cron, new Date(), options.timezone ?? this.config.timezone).toISOString();
    await this.createRecord(id, name, "cron", options.payload, options.priority, getMaxAttempts(options.retry), {
      nextRunAt,
      metadata: { cron: options.cron, timezone: options.timezone ?? this.config.timezone },
      status: options.enabled === false ? "paused" : "scheduled",
    });
    this.entries.set(id, {
      id,
      name,
      kind: "cron",
      task: options.task as JobTask,
      cron: options.cron,
      retry: options.retry,
      timeoutMs: options.timeout ?? this.config.defaultTimeout,
      preventOverlap: options.preventOverlap ?? this.config.preventOverlapping,
    });
    this.eventBus.emit("job:scheduled", { jobId: id, name, nextRunAt });
    if (this.running && options.enabled !== false) this.armCron(id);
    return id;
  }

  async delay<TPayload = unknown, TResult = unknown>(name: string, options: DelayJobOptions<TPayload, TResult>): Promise<string> {
    validateJobName(name);
    assertPositiveNumber(options.delayMs, "delayMs");
    assertTask(options.task);
    assertSerializablePayload(options.payload, this.config.maxPayloadBytes);
    const id = createJobId("delay");
    const nextRunAt = new Date(Date.now() + options.delayMs).toISOString();
    await this.createRecord(id, name, "delay", options.payload, options.priority, getMaxAttempts(options.retry), {
      nextRunAt,
      status: "scheduled",
    });
    this.entries.set(id, {
      id,
      name,
      kind: "delay",
      task: options.task as JobTask,
      delayMs: options.delayMs,
      retry: options.retry,
      timeoutMs: options.timeout ?? this.config.defaultTimeout,
      preventOverlap: false,
    });
    if (this.running) this.armDelay(id, options.delayMs);
    return id;
  }

  async every<TPayload = unknown, TResult = unknown>(name: string, options: EveryJobOptions<TPayload, TResult>): Promise<string> {
    validateJobName(name);
    assertPositiveNumber(options.intervalMs, "intervalMs");
    assertTask(options.task);
    assertSerializablePayload(options.payload, this.config.maxPayloadBytes);
    const id = createJobId("every");
    const nextRunAt = new Date(Date.now() + options.intervalMs).toISOString();
    await this.createRecord(id, name, "every", options.payload, options.priority, getMaxAttempts(options.retry), {
      nextRunAt,
      status: options.enabled === false ? "paused" : "scheduled",
    });
    this.entries.set(id, {
      id,
      name,
      kind: "every",
      task: options.task as JobTask,
      intervalMs: options.intervalMs,
      retry: options.retry,
      timeoutMs: options.timeout ?? this.config.defaultTimeout,
      preventOverlap: options.preventOverlap ?? this.config.preventOverlapping,
    });
    if (this.running && options.enabled !== false) this.armEvery(id);
    return id;
  }

  async runNow<TPayload = unknown, TResult = unknown>(name: string, options: RunNowOptions<TPayload, TResult>): Promise<JobResult<TResult>> {
    validateJobName(name);
    assertTask(options.task);
    assertSerializablePayload(options.payload, this.config.maxPayloadBytes);
    const id = createJobId("now");
    await this.createRecord(id, name, "runNow", options.payload, options.priority, getMaxAttempts(options.retry), {
      status: "pending",
    });
    this.entries.set(id, {
      id,
      name,
      kind: "runNow",
      task: options.task as JobTask,
      retry: options.retry,
      timeoutMs: options.timeout ?? this.config.defaultTimeout,
      preventOverlap: false,
    });
    return this.executeJob(id) as Promise<JobResult<TResult>>;
  }

  async runWorker<TPayload = unknown, TResult = unknown>(name: string, options: WorkerJobOptions<TPayload>): Promise<JobResult<TResult>> {
    validateJobName(name);
    assertSerializablePayload(options.payload, this.config.maxPayloadBytes);
    const id = createJobId("worker");
    await this.createRecord(id, name, "worker", options.payload, options.priority, getMaxAttempts(options.retry), {
      status: "pending",
    });
    const timeoutMs = options.timeout ?? this.config.defaultTimeout;
    const workerTask: JobTask<TPayload, TResult> =
      typeof options.worker === "function"
        ? (options.worker as JobTask<TPayload, TResult>)
        : async (context) =>
            this.workerPool.run<TPayload, TResult>({
              worker: options.worker as WorkerModuleReference,
              payload: context.payload,
              jobId: context.jobId,
              name: context.name,
              attempt: context.attempt,
              timeoutMs,
              createdAt: context.createdAt.toISOString(),
              startedAt: context.startedAt.toISOString(),
            });

    if (typeof options.worker === "function") {
      this.logger.warn(
        "runWorker received an inline function. For security, SilentCronX does not eval code strings; the function will run through the async runner. Pass { path, exportName } to use worker_threads."
      );
    }

    this.entries.set(id, {
      id,
      name,
      kind: "worker",
      task: workerTask as JobTask,
      retry: options.retry,
      timeoutMs,
      preventOverlap: false,
    });
    return this.executeJob(id) as Promise<JobResult<TResult>>;
  }

  queue<TPayload = unknown, TResult = unknown>(name: string, options: QueueOptions<TPayload, TResult> = {}): void {
    this.queueManager.createQueue(name, options as QueueOptions);
  }

  addJob<TPayload = unknown, TResult = unknown>(queueName: string, job: AddQueueJobOptions<TPayload, TResult>): Promise<string> {
    return this.queueManager.addJob(queueName, job);
  }

  pauseQueue(queueName: string): void {
    this.queueManager.pauseQueue(queueName);
  }

  resumeQueue(queueName: string): void {
    this.queueManager.resumeQueue(queueName);
  }

  clearQueue(queueName: string): Promise<number> {
    return this.queueManager.clearQueue(queueName);
  }

  getQueueStats(queueName?: string): QueueStats[] {
    const stats = this.queueManager.getQueueStats(queueName);
    for (const stat of stats) this.eventBus.emit("queue:stats", stat);
    return stats;
  }

  async cancel(jobId: string): Promise<void> {
    this.controllers.get(jobId)?.abort(new JobCancelledError());
    const job = await this.storage.getJob(jobId);
    if (!job) return;
    this.clearTimer(jobId);
    await this.storage.updateJob(jobId, { status: "cancelled", finishedAt: nowIso() });
    this.eventBus.emit("job:cancelled", { jobId, name: job.name });
  }

  async pause(jobId: string): Promise<void> {
    const job = await this.storage.getJob(jobId);
    if (!job) return;
    this.clearTimer(jobId);
    await this.storage.updateJob(jobId, { status: "paused" });
    this.eventBus.emit("job:paused", { jobId, name: job.name });
  }

  async resume(jobId: string): Promise<void> {
    const job = await this.storage.getJob(jobId);
    const entry = this.entries.get(jobId);
    if (!job || !entry) return;
    await this.storage.updateJob(jobId, { status: entry.kind === "runNow" ? "pending" : "scheduled" });
    this.eventBus.emit("job:resumed", { jobId, name: job.name });
    if (this.running) this.armEntry(jobId);
  }

  async getStatus(jobId: string): Promise<StatusSnapshot | null> {
    const job = await this.storage.getJob(jobId);
    if (!job) return null;
    return {
      jobId,
      name: job.name,
      status: job.status,
      attempt: job.attempts,
      nextRunAt: job.nextRunAt,
      lastRunAt: job.lastRunAt,
    };
  }

  getJobs(): Promise<JobRecord[]> {
    return this.storage.listJobs();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    for (const jobId of this.entries.keys()) this.armEntry(jobId);
    this.eventBus.emit("scheduler:started", { running: true, at: nowIso() });
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    for (const jobId of this.entries.keys()) this.clearTimer(jobId);
    this.eventBus.emit("scheduler:stopped", { running: false, at: nowIso() });
  }

  async shutdown(): Promise<void> {
    this.stop();
    for (const controller of this.controllers.values()) controller.abort(new JobCancelledError("Scheduler shutdown"));
    this.controllers.clear();
    await this.workerPool.shutdown();
    this.eventBus.emit("scheduler:shutdown", { at: nowIso() });
  }

  on<TName extends SilentCronXEventName>(eventName: TName, handler: SilentCronXEventHandler<TName>): void {
    this.eventBus.on(eventName, handler);
  }

  off<TName extends SilentCronXEventName>(eventName: TName, handler: SilentCronXEventHandler<TName>): void {
    this.eventBus.off(eventName, handler);
  }

  getEventHistory<TName extends SilentCronXEventName>(eventName?: TName): SilentCronXEventRecord[] {
    return this.eventBus.getHistory(eventName);
  }

  clearEventHistory(): void {
    this.eventBus.clearHistory();
  }

  async getHealth(): Promise<SilentCronXHealth> {
    const jobs = await this.storage.listJobs();
    return {
      running: this.running,
      scheduledJobs: jobs.filter((job) => job.status === "scheduled").length,
      runningJobs: jobs.filter((job) => job.status === "running").length,
      queuedJobs: this.queueManager.getQueuedCount(),
      workers: this.workerPool.getStats(),
      memory: {
        storage: this.storage instanceof MemoryStorageAdapter ? "memory" : "custom",
      },
    };
  }

  private async executeJob<TResult = unknown>(
    jobId: string,
    taskOverride?: JobTask,
    retryOverride?: RetryConfig,
    timeoutOverride?: number
  ): Promise<JobResult<TResult>> {
    while (this.runner.getActiveCount() >= this.config.maxConcurrency) {
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
    const record = await this.storage.getJob(jobId);
    const entry = this.entries.get(jobId);
    const task = taskOverride ?? entry?.task;
    if (!record || !task) throw new SilentCronXError(`Job cannot be executed: ${jobId}`);
    if (record.status === "cancelled" || record.status === "paused") {
      return { jobId, name: record.name, status: record.status, attempt: record.attempts, durationMs: 0 };
    }
    const controller = new AbortController();
    this.controllers.set(jobId, controller);
    try {
      return (await this.runner.run({
        record,
        task,
        retry: retryOverride ?? entry?.retry,
        timeoutMs: timeoutOverride ?? entry?.timeoutMs ?? this.config.defaultTimeout,
        signal: controller.signal,
        lockKey: entry?.preventOverlap ? `job:${record.name}` : undefined,
        lockTtlMs: this.config.lockTimeout,
        storage: this.storage,
        eventBus: this.eventBus,
        acquireLock: (key, ttl) => this.lockManager.acquire(key, ttl),
        releaseLock: (key) => this.lockManager.release(key),
      })) as JobResult<TResult>;
    } finally {
      this.controllers.delete(jobId);
    }
  }

  private armEntry(jobId: string): void {
    const entry = this.entries.get(jobId);
    if (!entry) return;
    if (entry.kind === "cron") this.armCron(jobId);
    else if (entry.kind === "delay") this.armDelay(jobId, entry.delayMs ?? 0);
    else if (entry.kind === "every") this.armEvery(jobId);
  }

  private async armCron(jobId: string): Promise<void> {
    const entry = this.entries.get(jobId);
    const record = await this.storage.getJob(jobId);
    if (!entry?.cron || !record || record.status === "paused" || record.status === "cancelled") return;
    const next = getNextCronDate(entry.cron, new Date(), this.config.timezone);
    await this.storage.updateJob(jobId, { status: "scheduled", nextRunAt: next.toISOString() });
    this.clearTimer(jobId);
    const timer = setTimeout(() => {
      void this.executeJob(jobId).finally(() => {
        if (this.running) void this.armCron(jobId);
      });
    }, Math.max(1, next.getTime() - Date.now()));
    timer.unref?.();
    entry.timer = timer;
  }

  private armDelay(jobId: string, delayMs: number): void {
    const entry = this.entries.get(jobId);
    if (!entry) return;
    this.clearTimer(jobId);
    const timer = setTimeout(() => void this.executeJob(jobId), delayMs);
    timer.unref?.();
    entry.timer = timer;
  }

  private async armEvery(jobId: string): Promise<void> {
    const entry = this.entries.get(jobId);
    const record = await this.storage.getJob(jobId);
    if (!entry?.intervalMs || !record || record.status === "paused" || record.status === "cancelled") return;
    const next = new Date(Date.now() + entry.intervalMs);
    await this.storage.updateJob(jobId, { status: "scheduled", nextRunAt: next.toISOString() });
    this.clearTimer(jobId);
    const timer = setTimeout(() => {
      void this.executeJob(jobId).finally(() => {
        if (this.running) void this.armEvery(jobId);
      });
    }, entry.intervalMs);
    timer.unref?.();
    entry.timer = timer;
  }

  private clearTimer(jobId: string): void {
    const entry = this.entries.get(jobId);
    if (entry?.timer) clearTimeout(entry.timer);
    if (entry) entry.timer = undefined;
  }

  private async createRecord<TPayload>(
    id: string,
    name: string,
    kind: JobRecord["kind"],
    payload: TPayload | undefined,
    priority = 0,
    maxAttempts = 1,
    extra: Partial<JobRecord> = {}
  ): Promise<void> {
    const createdAt = nowIso();
    await this.storage.saveJob({
      id,
      name,
      kind,
      status: extra.status ?? "pending",
      payload,
      priority,
      attempts: 0,
      maxAttempts,
      createdAt,
      updatedAt: createdAt,
      successCount: 0,
      failureCount: 0,
      ...extra,
    });
  }
}
